import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  createNarrativeTemplate,
  readNarrativeTemplates,
  updateNarrativeTemplate,
} from '@api/narratives';
import {
  Announcement,
  ButtonV2,
  GridPlusIcon,
  OverlayTriggerTooltip,
  toast,
  truncateTextWithEllipsis,
} from '@utilities';
import NarrativesTemplatesModal from '../NarrativesTemplatesModal';
import { OPTIONS } from '../NarrativesTemplatesModal/utilities/constants';

import styles from './_index.module.scss';

const SaveAsTemplate = ({
  canAccessSaveTemplate,
  isPublicShare,
  onTemplateSaved,
  topicIds = [],
  triggerClassName = '',
  triggerVariant = 'button',
}) => {
  const [error, setError] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(OPTIONS.CREATE);
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);

  const isCreateSelected = selectedOption === OPTIONS.CREATE;
  const isUpdateSelected = selectedOption === OPTIONS.UPDATE;
  const canRender = canAccessSaveTemplate && !isPublicShare;
  const hasTopicIds = Array.isArray(topicIds) && topicIds.length > 0;

  const templateOptions = templates.map((template) => ({
    description: template?.description || '',
    label: truncateTextWithEllipsis({ text: template?.name || 'Untitled Template' }),
    name: template?.name || 'Untitled Template',
    value: template?.id,
  }));

  useEffect(() => {
    if (isCreateSelected) {
      setSelectedTemplate(null);
      return;
    }

    if (!selectedTemplate) {
      setDescription('');
    }
  }, [isCreateSelected, selectedTemplate]);

  const getTemplates = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await readNarrativeTemplates();

      setTemplates(Array.isArray(response) ? response : []);
      setHasLoadedTemplates(true);
    } catch (requestError) {
      setError('Unable to load templates. Please try again.');
      console.error(requestError);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isActive || !isUpdateSelected || hasLoadedTemplates) return;
    getTemplates();
  }, [hasLoadedTemplates, isActive, isUpdateSelected]);

  useEffect(() => {
    if (isActive) return;

    setError('');
    setIsLoading(false);
    setIsSubmitting(false);
    setSelectedOption(OPTIONS.CREATE);
    setTemplateName('');
    setDescription('');
    setTemplates([]);
    setSelectedTemplate(null);
    setHasLoadedTemplates(false);
  }, [isActive]);

  if (!canRender) return null;

  const handleTemplateChange = (templateOption) => {
    setSelectedTemplate(templateOption);
    setDescription(templateOption?.description || '');
  };

  const handleSelectOption = (nextOption) => {
    setSelectedOption(nextOption);
    setError('');
    setDescription('');
    setSelectedTemplate(null);
  };

  const handleSubmit = async () => {
    setError('');

    if (!hasTopicIds) {
      setError('Template can be saved after topic cards finish loading.');
      return;
    }

    if (isUpdateSelected) {
      if (!selectedTemplate) return;

      try {
        setIsSubmitting(true);

        await updateNarrativeTemplate({
          description: description?.trim() || '',
          name: selectedTemplate.name || 'Untitled Template',
          templateId: selectedTemplate.value,
          topicIds,
        });

        toast.success(
          `Template "${selectedTemplate.name || 'Untitled Template'}" updated successfully.`
        );
        setIsActive(false);
      } catch (requestError) {
        setError(
          requestError?.response?.data?.[0] || 'Unable to update template. Please try again.'
        );
        console.error(requestError);
      } finally {
        setIsSubmitting(false);
      }

      return;
    }
    try {
      setIsSubmitting(true);

      await createNarrativeTemplate({
        description: description?.trim() || '',
        name: templateName.trim(),
        topicIds,
      });

      toast.success(`Template "${templateName.trim()}" saved successfully.`);
      onTemplateSaved?.({
        description: description?.trim() || '',
        id: `temp-template-${Date.now()}`,
        name: templateName.trim(),
        topicIds: [...topicIds],
      });
      setIsActive(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.[0] || 'Unable to save template. Please try again.');
      console.error(requestError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled =
    (isCreateSelected ? templateName.trim().length === 0 || !hasTopicIds : !selectedTemplate) ||
    isLoading ||
    isSubmitting;

  return (
    <>
      {triggerVariant === 'icon' ? (
        <OverlayTriggerTooltip aria-label="Save as Template" content="Save as Template">
          <button
            className={triggerClassName}
            data-testid="save-as-template-icon"
            onClick={() => setIsActive(true)}
            type="button"
          >
            <GridPlusIcon />
          </button>
        </OverlayTriggerTooltip>
      ) : (
        <ButtonV2
          data-testid="save-as-template-button"
          onClick={() => setIsActive(true)}
          shape="round"
        >
          Save as Template
        </ButtonV2>
      )}

      <NarrativesTemplatesModal
        cancelButtonDataTestId="save-as-template-cancel-button"
        className={styles['save-as-template']}
        contentClassName={styles['save-as-template-form-fields']}
        createOptionDataTestId="save-as-template-option-create"
        dataTestId="save-as-template-modal"
        description={description}
        descriptionDataTestId={
          isCreateSelected
            ? 'save-as-template-input-description'
            : 'save-as-template-update-description'
        }
        descriptionIsDisabled={!selectedTemplate}
        error={
          error ? (
            <Announcement data-testid="save-as-template-error" text={error} variant="error" />
          ) : null
        }
        fieldClassName={styles['save-as-template-field']}
        fieldControlClassName={styles['save-as-template-field-control']}
        formMode="create-update"
        isActive={isActive}
        isLoading={isLoading}
        isSubmitDisabled={isSubmitDisabled}
        isSubmitting={isSubmitting}
        isTemplateNameRequired
        nameInputDataTestId="save-as-template-input-name"
        onClose={() => setIsActive(false)}
        onDescriptionChange={setDescription}
        onOptionChange={handleSelectOption}
        onSubmit={handleSubmit}
        onTemplateNameChange={setTemplateName}
        onTemplateSelectChange={handleTemplateChange}
        selectedOption={selectedOption}
        selectedTemplate={selectedTemplate}
        submitButtonDataTestId="save-as-template-save-button"
        subtext="Create a new template or update an existing one"
        subtextDataTestId="save-as-template-subtext"
        templateName={templateName}
        templateOptions={templateOptions}
        templateSelectDataTestId="save-as-template-select-name"
        title="Save as Template"
        updateOptionDataTestId="save-as-template-option-update"
      />
    </>
  );
};

SaveAsTemplate.propTypes = {
  canAccessSaveTemplate: PropTypes.bool,
  isPublicShare: PropTypes.bool,
  onTemplateSaved: PropTypes.func,
  topicIds: PropTypes.arrayOf(PropTypes.string),
  triggerClassName: PropTypes.string,
  triggerVariant: PropTypes.oneOf(['button', 'icon']),
};

export default SaveAsTemplate;
